import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const SearchBar = ({ searchQuery, onSearchChange, suggestions = [] }) => {
  const { t } = useTranslation('conferences');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions?.length === 0) return;

    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setFocusedIndex((prev) =>
          prev < suggestions?.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e?.preventDefault();
        if (focusedIndex >= 0) {
          handleSuggestionClick(suggestions?.[focusedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSuggestionClick = (suggestion) => {
    onSearchChange(suggestion);
    setShowSuggestions(false);
    setFocusedIndex(-1);
  };

  const handleInputChange = (e) => {
    onSearchChange(e?.target?.value);
    setShowSuggestions(true);
    setFocusedIndex(-1);
  };

  const handleInputFocus = () => {
    if (suggestions?.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <div ref={searchRef} className="relative w-full">
      <div className="relative">
        <div className="absolute start-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <Icon name="Search" size={20} className="text-muted-foreground" />
        </div>
        <Input
          type="search"
          placeholder={t('publicBoard.searchPlaceholder')}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className="ps-12"
        />
      </div>
      {showSuggestions && suggestions?.length > 0 && (
        <div className="absolute top-full inset-x-0 mt-2 bg-popover border border-border shadow-elevation-3 overflow-hidden z-50 max-h-80 overflow-y-auto scrollbar-thin">
          {suggestions?.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`w-full text-start px-4 py-3 hover:bg-muted transition-250 flex items-center gap-3 ${
                index === focusedIndex ? 'bg-muted' : ''
              }`}
            >
              <Icon name="Search" size={16} className="text-muted-foreground flex-shrink-0" />
              <span className="text-sm text-foreground">{suggestion}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
